import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import { saveAs } from "file-saver";
import { IManagedDbRow } from "../../services/table-editor-service";
import { TableEditorSqlDialect } from "./table-editor-dialect";

// KOA-5857 — Data extractors used by the right-click "Export" menu.
//
// Pure functions where possible (the SQL + CSV emitters take no React, no
// FS, no library) so they can be unit-tested without dom. The XLSX emitter
// lazy-loads exceljs to keep the main bundle small — same pattern as
// import-excel-modal.tsx elsewhere in the designer.

// ── SQL-literal escaping ─────────────────────────────────────────────
// Conservative: emits T-SQL-compatible literals that also parse on Postgres
// and Oracle (single-quoted strings with doubled quotes, ISO 8601 datetimes,
// bare numbers and bools). Designed for human review / one-off migration,
// not for high-throughput data pipelines.
// Types JS Number can represent exactly. Int32 stays safely under 2^31,
// Int16 under 2^15, Double IS a JS number — coercing through Number()
// is round-trip safe for these.
const SAFE_NUMERIC_TYPES: ReadonlySet<string> = new Set<string>(["Int16", "Int32", "Double", "AutoIncrement"]);

// Types where JS Number coercion silently truncates precision. Int64
// loses values past 2^53 (e.g. 9007199254740993 → 9007199254740992),
// high-scale Decimal loses trailing digits. The backend preserves
// these as strings on the wire (MaterializeRows in
// DatasourceService.TableEditor.cs), so the safe path is to emit the
// source string verbatim when it looks numeric — never round-trip
// through Number().
const HIGH_PRECISION_NUMERIC_TYPES: ReadonlySet<string> = new Set<string>(["Int64", "Decimal"]);

// Permissive regex covering int (Int64) and fixed-point Decimal shapes.
// Sign + digits + optional fractional part. Scientific notation isn't
// valid SQL numeric syntax everywhere, and the backend doesn't emit it
// for these types — reject so we quote-as-text rather than emitting
// something the SQL parser might mis-handle.
const NUMERIC_STRING_PATTERN: RegExp = /^-?\d+(\.\d+)?$/;

export const sqlLiteral = (value: unknown, column?: IDatabaseColumnModel): string => {
  if (value === null || value === undefined) {
    return "NULL";
  }
  if (typeof value === "boolean") {
    return value ? "1" : "0";
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "NULL";
  }
  const type: string = column?.kuikaDataType ?? "";
  const asString: string = String(value);
  if (type === "Boolean") {
    return asString === "true" || asString === "1" ? "1" : "0";
  }
  if (HIGH_PRECISION_NUMERIC_TYPES.has(type)) {
    // Preserve the source string when it parses as a numeric literal.
    // Anything that doesn't match the shape falls back to a quoted
    // string so we don't emit garbage as a numeric.
    if (NUMERIC_STRING_PATTERN.test(asString)) {
      return asString;
    }
    return `'${asString.replace(/'/g, "''")}'`;
  }
  if (SAFE_NUMERIC_TYPES.has(type)) {
    const n: number = Number(asString);
    return Number.isFinite(n) ? String(n) : `'${asString.replace(/'/g, "''")}'`;
  }
  // Strings, Guids, dates — single-quoted with doubled quotes inside.
  return `'${asString.replace(/'/g, "''")}'`;
};

const quoteIdent = (name: string, dialect: TableEditorSqlDialect): string => {
  if (dialect === "sqlserver") {
    return `[${name.replace(/]/g, "]]")}]`;
  }
  return `"${name.replace(/"/g, '""')}"`;
};

const qualifyTable = (tableName: string, tableSchema: string | undefined, dialect: TableEditorSqlDialect): string => {
  if (dialect === "oracle") {
    return quoteIdent(tableName, dialect);
  }
  if (tableSchema && tableSchema.length > 0) {
    return `${quoteIdent(tableSchema, dialect)}.${quoteIdent(tableName, dialect)}`;
  }
  if (dialect === "sqlserver") {
    return `${quoteIdent("dbo", dialect)}.${quoteIdent(tableName, dialect)}`;
  }
  return quoteIdent(tableName, dialect);
};

// ── CSV (RFC 4180) ───────────────────────────────────────────────────
export const csvCell = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  let s: string = typeof value === "boolean" ? (value ? "true" : "false") : String(value);
  // CSV formula-injection mitigation: a cell starting with =, +, -, @, TAB,
  // CR, or LF is interpreted as a formula by Excel / Google Sheets /
  // LibreOffice. Prefix with a single quote so the spreadsheet renders it as
  // text. Applied before the comma/quote escape so the prefix lives inside
  // the quoted field when the cell also contains a comma.
  if (s.length > 0) {
    const first: string = s[0]!;
    if (
      first === "=" ||
      first === "+" ||
      first === "-" ||
      first === "@" ||
      first === "\t" ||
      first === "\r" ||
      first === "\n"
    ) {
      s = "'" + s;
    }
  }
  // Quote if the cell contains a comma, double-quote, CR, or LF.
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

export const rowsToCsv = (columns: IDatabaseColumnModel[], rows: IManagedDbRow[]): string => {
  const header: string = columns.map((c: IDatabaseColumnModel): string => csvCell(c.name)).join(",");
  const body: string[] = rows.map((row: IManagedDbRow): string =>
    columns.map((c: IDatabaseColumnModel): string => csvCell(row[c.name])).join(",")
  );
  // RFC 4180 uses CRLF line endings.
  return [header, ...body].join("\r\n") + "\r\n";
};

// ── SQL INSERT ───────────────────────────────────────────────────────
export interface IExportContext {
  tableName: string;
  tableSchema?: string;
  sqlDialect?: TableEditorSqlDialect;
  columns: IDatabaseColumnModel[];
  primaryKeyColumns: string[];
}

const exportDialect = (ctx: IExportContext): TableEditorSqlDialect => ctx.sqlDialect ?? "sqlserver";

export const rowsToSqlInserts = (ctx: IExportContext, rows: IManagedDbRow[]): string => {
  const dialect: TableEditorSqlDialect = exportDialect(ctx);
  const cols: string[] = ctx.columns.map((c: IDatabaseColumnModel): string => quoteIdent(c.name, dialect));
  const table: string = qualifyTable(ctx.tableName, ctx.tableSchema, dialect);
  const lines: string[] = rows.map((row: IManagedDbRow): string => {
    const vals: string[] = ctx.columns.map((c: IDatabaseColumnModel): string => sqlLiteral(row[c.name], c));
    return `INSERT INTO ${table} (${cols.join(", ")}) VALUES (${vals.join(", ")});`;
  });
  return lines.join("\n") + "\n";
};

// ── SQL UPDATE ───────────────────────────────────────────────────────
export const rowsToSqlUpdates = (ctx: IExportContext, rows: IManagedDbRow[]): string => {
  if (ctx.primaryKeyColumns.length === 0) {
    throw new Error("UPDATE export requires a primary key on the table.");
  }
  const dialect: TableEditorSqlDialect = exportDialect(ctx);
  const pkSet: Set<string> = new Set(ctx.primaryKeyColumns);
  const setCols: IDatabaseColumnModel[] = ctx.columns.filter((c: IDatabaseColumnModel): boolean => !pkSet.has(c.name));
  if (setCols.length === 0) {
    // PK-only table — every column is part of the key, so there's
    // nothing to SET. Emitting `UPDATE x SET WHERE pk=...` would be
    // syntactically invalid SQL. The toolbar / context menu disable
    // the action for this shape; this throw is the belt-and-braces
    // for direct callers (e.g. clone-source export).
    throw new Error("UPDATE export requires at least one non-primary-key column.");
  }
  const table: string = qualifyTable(ctx.tableName, ctx.tableSchema, dialect);
  const lines: string[] = rows.map((row: IManagedDbRow): string => {
    const setClause: string = setCols
      .map((c: IDatabaseColumnModel): string => `${quoteIdent(c.name, dialect)} = ${sqlLiteral(row[c.name], c)}`)
      .join(", ");
    const whereClause: string = ctx.primaryKeyColumns
      .map((name: string): string => {
        const col = ctx.columns.find((c: IDatabaseColumnModel): boolean => c.name === name);
        return `${quoteIdent(name, dialect)} = ${sqlLiteral(row[name], col)}`;
      })
      .join(" AND ");
    return `UPDATE ${table} SET ${setClause} WHERE ${whereClause};`;
  });
  return lines.join("\n") + "\n";
};

// ── SQL UPSERT — per-dialect ─────────────────────────────────────────
// SQL Server: T-SQL MERGE with AS aliasing, trailing `;`.
// Oracle:     MERGE without AS, parenthesised ON, FROM DUAL source,
//             no trailing `;` (Oracle rejects ';' on single-stmt protocol).
// Postgres:   INSERT ... ON CONFLICT (pk) DO UPDATE SET col = EXCLUDED.col.
//             Portable since 9.5; cleaner row-by-row semantics than the
//             MERGE form Postgres 15+ added.
// The bodies mirror the backend connector helpers
// (BuildUpsertStatementForExport on ISqlDatabaseConnector) so the
// client-side selection/page export matches what the server's full-table
// streaming export emits.
export const rowsToSqlUpserts = (ctx: IExportContext, rows: IManagedDbRow[]): string => {
  if (ctx.primaryKeyColumns.length === 0) {
    throw new Error("UPSERT export requires a primary key on the table.");
  }
  const dialect: TableEditorSqlDialect = exportDialect(ctx);
  const pkSet: Set<string> = new Set(ctx.primaryKeyColumns);
  const nonPkCols: IDatabaseColumnModel[] = ctx.columns.filter(
    (c: IDatabaseColumnModel): boolean => !pkSet.has(c.name)
  );
  const table: string = qualifyTable(ctx.tableName, ctx.tableSchema, dialect);

  if (dialect === "postgres") {
    const quotedAll: string[] = ctx.columns.map((c: IDatabaseColumnModel): string => quoteIdent(c.name, dialect));
    const quotedPks: string[] = ctx.primaryKeyColumns.map((n: string): string => quoteIdent(n, dialect));
    const lines: string[] = rows.map((row: IManagedDbRow): string => {
      const vals: string[] = ctx.columns.map((c: IDatabaseColumnModel): string => sqlLiteral(row[c.name], c));
      const insert: string = `INSERT INTO ${table} (${quotedAll.join(", ")}) VALUES (${vals.join(", ")})`;
      if (nonPkCols.length === 0) {
        return `${insert} ON CONFLICT (${quotedPks.join(", ")}) DO NOTHING;`;
      }
      const updateSet: string = nonPkCols
        .map(
          (c: IDatabaseColumnModel): string =>
            `${quoteIdent(c.name, dialect)} = EXCLUDED.${quoteIdent(c.name, dialect)}`
        )
        .join(", ");
      return `${insert} ON CONFLICT (${quotedPks.join(", ")}) DO UPDATE SET ${updateSet};`;
    });
    return lines.join("\n") + "\n";
  }

  // SQL Server and Oracle MERGE — same shape with three dialect deltas.
  const isOracle: boolean = dialect === "oracle";
  const targetAlias: string = isOracle ? `${table} T` : `${table} AS T`;
  const sourceAliasSuffix: string = isOracle ? " FROM DUAL) S" : ") AS S";
  const onWrap = (clause: string): string => (isOracle ? `(${clause})` : clause);
  const terminator: string = isOracle ? "" : ";";
  const allCols: string[] = ctx.columns.map((c: IDatabaseColumnModel): string => quoteIdent(c.name, dialect));
  const lines: string[] = rows.map((row: IManagedDbRow): string => {
    const vals: string[] = ctx.columns.map((c: IDatabaseColumnModel): string => sqlLiteral(row[c.name], c));
    const onClause: string = ctx.primaryKeyColumns
      .map((name: string): string => `T.${quoteIdent(name, dialect)} = S.${quoteIdent(name, dialect)}`)
      .join(" AND ");
    const updateClause: string =
      nonPkCols.length > 0
        ? " WHEN MATCHED THEN UPDATE SET " +
          nonPkCols
            .map(
              (c: IDatabaseColumnModel): string => `${quoteIdent(c.name, dialect)} = S.${quoteIdent(c.name, dialect)}`
            )
            .join(", ")
        : "";
    const insertCols: string = allCols.join(", ");
    const insertVals: string = allCols.map((c: string): string => `S.${c}`).join(", ");
    const sourceSelect: string = ctx.columns
      .map((c: IDatabaseColumnModel, i: number): string => `${vals[i]} AS ${quoteIdent(c.name, dialect)}`)
      .join(", ");
    return `MERGE INTO ${targetAlias} USING (SELECT ${sourceSelect}${sourceAliasSuffix} ON ${onWrap(onClause)}${updateClause} WHEN NOT MATCHED THEN INSERT (${insertCols}) VALUES (${insertVals})${terminator}`;
  });
  return lines.join("\n") + "\n";
};

// ── XLSX (lazy-loaded exceljs) ───────────────────────────────────────
export const downloadXlsx = async (
  filename: string,
  columns: IDatabaseColumnModel[],
  rows: IManagedDbRow[]
): Promise<void> => {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Sheet1");
  sheet.columns = columns.map((c: IDatabaseColumnModel) => ({
    header: c.name,
    key: c.name,
    width: Math.max(c.name.length + 2, 14)
  }));
  // Bold header row.
  sheet.getRow(1).font = { bold: true };
  rows.forEach((row: IManagedDbRow): void => {
    const ordered: { [key: string]: unknown } = {};
    columns.forEach((c: IDatabaseColumnModel): void => {
      const v: unknown = row[c.name];
      ordered[c.name] = v === undefined ? null : v;
    });
    sheet.addRow(ordered);
  });
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  saveAs(blob, filename);
};

// ── Text-file download helper ────────────────────────────────────────
export const downloadText = (filename: string, mime: string, contents: string): void => {
  // Prepend a UTF-8 BOM for CSVs so Excel opens them with the right encoding.
  const body: string = mime === "text/csv" ? "﻿" + contents : contents;
  const blob = new Blob([body], { type: `${mime};charset=utf-8` });
  saveAs(blob, filename);
};

export const downloadBlob = (filename: string, blob: Blob): void => {
  saveAs(blob, filename);
};

// ── Streaming download ───────────────────────────────────────────────
// Fetches `url` and pipes the response body directly to disk via the
// File System Access API (`showSaveFilePicker`). No accumulation in
// browser memory, so multi-GB CSV / SQL / XLSX exports don't risk
// freezing or OOM-ing the tab.
//
// `showSaveFilePicker` is Chromium-only (Chrome, Edge, Opera). Returns
// `false` from this helper when the browser doesn't support it so the
// caller can fall back to the buffered-blob path; throws on user
// cancellation OR an HTTP error so the caller can surface the failure
// through its existing error alert.
//
// Auth: the caller passes the full header bag that Axios would have
// stamped on the equivalent request (Authorization, domainName,
// currentProjectId, X-ClientId). Without all of them, endpoints
// gated by [KCurrentProjectIdInHeader] (which includes this export)
// reject the call. Use `authHeadersForFetch()` from
// `designer/src/auth/fetch-headers.ts` so the streaming path stays
// indistinguishable from an Axios-shaped request — see that file for
// the canonical header set.
export interface IStreamingDownloadOptions {
  url: string;
  suggestedFilename: string;
  // Headers identical to what Axios's interceptor would attach.
  // Spread directly into the fetch `headers` object; omit (or pass
  // an empty object) to make an anonymous call.
  authHeaders: Record<string, string>;
  // Optional MIME hint used in the OS save dialog so the user sees
  // the right extension grouping. Defaults to "*/*".
  mimeType?: string;
  // Optional AbortSignal — when triggered, aborts the fetch and the
  // pipe-to-disk so the caller can offer a Cancel affordance during
  // long-running streaming downloads.
  signal?: AbortSignal;
}

interface IFsaWritableShape {
  write: (chunk: ArrayBuffer | ArrayBufferView | Blob | string) => Promise<void>;
  close: () => Promise<void>;
  abort?: () => Promise<void>;
}
interface IFsaHandleShape {
  createWritable: () => Promise<IFsaWritableShape>;
}
interface IFsaPickerWindow extends Window {
  showSaveFilePicker?: (options: {
    suggestedName?: string;
    types?: { description?: string; accept: { [mime: string]: string[] } }[];
  }) => Promise<IFsaHandleShape>;
}

const extensionForFilename = (filename: string): string => {
  const dot: number = filename.lastIndexOf(".");
  return dot === -1 ? "" : filename.slice(dot);
};

// Cap on the error-response body we'll read on a failed fetch. The
// controller returns small JSON alerts (a few hundred bytes typically);
// a 64 KB ceiling is generous for those without risking an OOM if the
// server somehow returns a huge body.
const STREAMING_ERROR_BODY_CAP_BYTES: number = 64 * 1024;

// Shape that mirrors AxiosError closely enough for
// `buildTableEditorErrorAlert` / `extractError` to walk it the same
// way they walk a real AxiosError. We can't import AxiosError
// itself here (fetch path is meant to stay axios-free) so the shape
// is duplicated minimally.
interface IStreamingFetchErrorResponse {
  status: number;
  data: unknown;
}
interface IStreamingFetchError {
  message: string;
  code: string;
  response: IStreamingFetchErrorResponse;
}

// Read up to STREAMING_ERROR_BODY_CAP_BYTES of the error response and
// try to JSON-parse it. Falls back to the trimmed text body on parse
// failure. Returns null when there's no body at all. Defensive against
// huge / malformed payloads — the cap is what keeps the read bounded.
const readBoundedErrorBody = async (response: Response): Promise<unknown> => {
  if (response.body === null) {
    return null;
  }
  try {
    const reader: ReadableStreamDefaultReader<Uint8Array> = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let total: number = 0;
    while (total < STREAMING_ERROR_BODY_CAP_BYTES) {
      const next: ReadableStreamReadResult<Uint8Array> = await reader.read();
      if (next.done) {
        break;
      }
      chunks.push(next.value);
      total += next.value.length;
    }
    // If we hit the cap we stopped mid-stream; that's fine for
    // surfacing an error message — JSON.parse will reject and we'll
    // fall back to the partial text body anyway.
    try {
      await reader.cancel();
    } catch {
      // Best-effort — ignore.
    }
    const total_len: number = chunks.reduce((acc: number, c: Uint8Array): number => acc + c.length, 0);
    const merged: Uint8Array = new Uint8Array(total_len);
    let offset: number = 0;
    for (const chunk of chunks) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    const text: string = new TextDecoder("utf-8", { fatal: false }).decode(merged);
    if (text.trim().length === 0) {
      return null;
    }
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  } catch {
    return null;
  }
};

export const downloadStreaming = async (options: IStreamingDownloadOptions): Promise<boolean> => {
  const picker: IFsaPickerWindow = window as IFsaPickerWindow;
  if (typeof picker.showSaveFilePicker !== "function") {
    return false;
  }
  const ext: string = extensionForFilename(options.suggestedFilename);
  const acceptType: { [mime: string]: string[] } = {};
  acceptType[options.mimeType ?? "*/*"] = ext.length > 0 ? [ext] : [];
  let handle: IFsaHandleShape;
  try {
    handle = await picker.showSaveFilePicker({
      suggestedName: options.suggestedFilename,
      types: [{ description: "Exported table data", accept: acceptType }]
    });
  } catch (err: unknown) {
    // User cancelled the save dialog. Treat as a clean no-op so the
    // caller doesn't surface an error alert for what's a normal flow.
    if (err instanceof DOMException && err.name === "AbortError") {
      return true;
    }
    throw err;
  }
  const response: Response = await fetch(options.url, {
    method: "GET",
    headers: { ...options.authHeaders },
    signal: options.signal
  });
  if (!response.ok) {
    // Read the response body (capped) so callers can surface the
    // controller's actual error message — typically a TableEditorAlert
    // like "Export requires a primary key" — instead of a useless
    // "HTTP 400". Throw an Axios-shaped error so the existing
    // `buildTableEditorErrorAlert` path walks the response the same
    // way it walks an Axios failure on the buffered-blob fallback.
    const errorBody: unknown = await readBoundedErrorBody(response);
    const fetchError: IStreamingFetchError = {
      message: `Export request failed with HTTP ${response.status}`,
      code: response.status >= 500 ? "ERR_BAD_RESPONSE" : "ERR_BAD_REQUEST",
      response: { status: response.status, data: errorBody }
    };
    throw fetchError;
  }
  if (response.body === null) {
    throw new Error("Your browser couldn't receive the export. Try downloading again.");
  }
  const writable: IFsaWritableShape = await handle.createWritable();
  try {
    // Modern browsers support stream pipeTo on a WritableStream from
    // createWritable() — the entire transfer never lives in JS memory.
    await (response.body as unknown as { pipeTo: (dest: IFsaWritableShape) => Promise<void> }).pipeTo(writable);
  } catch (err: unknown) {
    if (writable.abort !== undefined) {
      try {
        await writable.abort();
      } catch {
        // Ignored — the original error is what the caller needs.
      }
    }
    throw err;
  }
  return true;
};

// ── Filename helper ──────────────────────────────────────────────────
export const exportFilename = (tableName: string, ext: string): string => {
  const ts: string = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return `${tableName}-${ts}.${ext}`;
};
